import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Wrench, FileText, Check, AlertTriangle, Loader2,
  CheckCircle2, Phone, Mail, MapPin, Car as CarIcon, MessageCircle,
} from "lucide-react";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = "41229809393"; // wa.me number sans +

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-CH", {
    day: "2-digit", month: "long", year: "numeric",
  });
}

function fmtMoney(v) {
  return (v || 0).toFixed(2);
}

export default function PublicDevis() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [decision, setDecision] = useState(null); // 'accept' | null
  const [confirmed, setConfirmed] = useState(false);
  const [conditionsAcc, setConditionsAcc] = useState(false);
  const pdfUrl = `${BACKEND_URL}/api/public/devis/${token}/pdf`;

  useEffect(() => {
    const load = async () => {
      try {
        const { data: d } = await axios.get(`${BACKEND_URL}/api/public/devis/${token}`);
        setData(d);
      } catch (e) {
        setError(e.response?.data?.detail || "Erreur");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const submit = async (dec) => {
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/devis/${token}/respond`, {
        decision: dec,
        message: null,
      });
      // iter89 — Signature digitale : enregistre IP + User-Agent + timestamp
      // Best-effort : ne bloque pas le flux si l'endpoint échoue.
      if (dec === "accept" && data?.or_id) {
        try {
          await axios.post(`${BACKEND_URL}/api/devis/${data.or_id}/approve`, {
            disclaimer_accepted: true,
          });
        } catch { /* trace silencieuse */ }
      }
      setDecision(dec);
      setConfirmed(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur de soumission, veuillez réessayer");
    } finally {
      setSubmitting(false);
    }
  };

  // iter132 — Bouton "J'ai une question" : redirection WhatsApp avec message pré-rempli
  const whatsappQuestionUrl = data
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        `Bonjour, j'ai une question sur le devis n° ${data.numero}`,
      )}`
    : "#";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f0f9f4] to-white">
        <Loader2 className="w-8 h-8 animate-spin text-[#52a675]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-white p-6">
        <div className="max-w-md text-center" data-testid="public-devis-error">
          <div className="w-16 h-16 mx-auto rounded-full bg-orange-100 flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-orange-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Lien indisponible</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            Merci de contacter votre garage pour obtenir un nouveau lien.
          </p>
        </div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f0f9f4] to-white p-6">
        <div className="max-w-md w-full text-center bg-white p-8 rounded-2xl shadow-xl border border-gray-100" data-testid="approval-confirmation">
          <div className="w-20 h-20 mx-auto rounded-full bg-[#52a675]/15 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-12 h-12 text-[#52a675]" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Devis accepté !</h1>
          <p className="text-gray-600 mb-6">
            Merci ! {data.garage.name} a été notifié et démarrera les travaux selon le planning convenu.
          </p>
          <div className="text-sm text-gray-500 space-y-1 border-t border-gray-100 pt-4">
            <div className="font-semibold text-gray-700">{data.garage.name}</div>
            <div className="flex items-center justify-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {data.garage.tel}</div>
            <div className="flex items-center justify-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {data.garage.email}</div>
          </div>
        </div>
      </div>
    );
  }

  const t = data.totaux || {};
  const accent = data.couleur || "#52a675";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f9f4]/40 to-white pb-20" data-testid="public-devis-page">
      {/* Header bandeau */}
      <header className="text-white py-5 px-4 shadow-md" style={{ backgroundColor: accent }}>
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-12 h-12 rounded-md bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
            <img
              src={`${BACKEND_URL}${data.logo_url || ""}`}
              alt={data.garage.name}
              className="w-full h-full object-contain p-1"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="devis-logo"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider opacity-80">Devis n°{data.numero}</div>
            <div className="font-bold text-lg truncate">{data.garage.name}</div>
          </div>
          {data.suivi_url && (
            <a
              href={data.suivi_url}
              className="text-[11px] underline whitespace-nowrap opacity-90 hover:opacity-100"
              data-testid="devis-suivi-link"
            >
              Suivre mon véhicule
            </a>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-6 space-y-5">
        {/* Récap */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" data-testid="devis-recap">
          <h1 className="text-xl font-heading font-bold text-gray-900 mb-1">Bonjour {data.client_nom},</h1>
          <p className="text-sm text-gray-600 mb-5">
            Voici le devis préparé par {data.garage.name}{data.vehicule?.plaque ? ` pour votre véhicule ${data.vehicule.plaque}` : ""}. Prenez le temps de le consulter, puis acceptez-le ou refusez-le ci-dessous. Une question ? Appelez-nous au {data.garage.tel}.
          </p>

          {/* iter245 — Statut du dossier, consultable pendant toute la durée des travaux */}
          {data.statut_label && (
            <div
              className="rounded-lg px-3 py-2.5 mb-3 text-sm flex items-center gap-2"
              style={{ backgroundColor: `${accent}12`, color: accent }}
              data-testid="devis-statut"
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: accent }} />
              <span className="font-semibold">{data.statut_label}</span>
            </div>
          )}

          {data.vehicule && (
            <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3 mb-3" data-testid="devis-vehicule">
              <CarIcon className="w-5 h-5 text-[#52a675] flex-shrink-0" />
              <div className="text-sm">
                <div className="font-semibold">{[data.vehicule.marque, data.vehicule.modele].filter(Boolean).join(" ")}</div>
                {data.vehicule.plaque && <div className="text-xs text-gray-500">{data.vehicule.plaque}</div>}
              </div>
            </div>
          )}

          {data.notes_client && (
            <div className="bg-amber-50 border-l-4 border-amber-300 rounded p-3 text-sm text-amber-900 mb-3">
              <div className="font-semibold text-xs uppercase tracking-wider text-amber-700 mb-1">Motif d&apos;intervention</div>
              {data.notes_client}
            </div>
          )}

          {/* Lignes */}
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 text-xs uppercase font-semibold text-gray-500 flex">
              <div className="flex-1">Description</div>
              <div className="w-24 text-right">Total HT</div>
            </div>
            {data.lignes.map((l, i) => (
              <div key={i} className="px-3 py-2.5 flex border-t border-gray-50" data-testid={`devis-ligne-${i}`}>
                <div className="flex-1 text-sm">
                  <div className="font-medium text-gray-900">{l.libelle}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {l.quantite}{l.unite ? ` ${l.unite}` : ""} × {fmtMoney(l.prix_unitaire_ht)} CHF
                    {l.rabais_pct > 0 && <span className="text-orange-600 ml-2">−{l.rabais_pct}%</span>}
                  </div>
                </div>
                <div className="w-24 text-right font-mono font-semibold">{fmtMoney(l.total_ht)}</div>
              </div>
            ))}
          </div>

          {/* Totaux */}
          <div className="mt-4 space-y-1 text-sm" data-testid="devis-totaux">
            <div className="flex justify-between text-gray-600"><span>Sous-total HT</span><span className="font-mono">{fmtMoney(t.sous_total_ht)}</span></div>
            {t.rabais_lignes_total > 0 && <div className="flex justify-between text-gray-500"><span>Rabais lignes</span><span className="font-mono">−{fmtMoney(t.rabais_lignes_total)}</span></div>}
            {t.rabais_global > 0 && <div className="flex justify-between text-gray-500"><span>Rabais global</span><span className="font-mono">−{fmtMoney(t.rabais_global)}</span></div>}
            <div className="flex justify-between border-t border-gray-100 pt-1"><span className="font-semibold">Total HT</span><span className="font-mono font-semibold">{fmtMoney(t.total_ht)}</span></div>
            <div className="flex justify-between text-gray-500"><span>TVA 8.1 %</span><span className="font-mono">+{fmtMoney(t.tva)}</span></div>
            <div className="flex justify-between text-lg pt-2 mt-1 border-t-2 border-[#52a675]">
              <span className="font-bold text-gray-900">Total TTC</span>
              <span className="font-mono font-bold text-[#2d5f3f]" data-testid="devis-total-ttc">{fmtMoney(t.total_ttc)} CHF</span>
            </div>
          </div>

          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            data-testid="devis-pdf-link"
          >
            <FileText className="w-4 h-4" /> Télécharger le devis (PDF)
          </a>
        </section>

        {/* Validation */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" data-testid="devis-accept-section">
          <h2 className="text-base font-heading font-bold text-gray-900 mb-3">Validation du devis</h2>
          {/* iter89 — Disclaimer signature digitale (CO art. 14) */}
          <p className="text-xs text-gray-500 mb-3 leading-relaxed border-l-2 border-gray-200 pl-3">
            En approuvant ce devis, vous acceptez nos Conditions Générales et confirmez votre accord pour les travaux décrits ci-dessus.
            Cette approbation constitue une signature numérique conformément à la loi suisse (CO art. 14).
            Votre adresse IP et l&apos;horodatage seront enregistrés à des fins légales.
          </p>
          <label className="flex items-start gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={conditionsAcc}
              onChange={(e) => setConditionsAcc(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#52a675]"
              data-testid="approve-checkbox"
            />
            <span className="text-sm text-gray-700">
              J&apos;ai lu et j&apos;accepte les conditions. J&apos;autorise {data.garage.name} à réaliser les travaux décrits pour un montant total TTC de <span className="font-bold">{fmtMoney(t.total_ttc)} CHF</span>.
            </span>
          </label>
          <button
            onClick={() => submit("accept")}
            disabled={!conditionsAcc || submitting}
            className="w-full py-3.5 rounded-lg bg-[#52a675] hover:bg-[#3d8a5c] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="approve-devis-btn"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
            {submitting ? "Envoi…" : "Approuver ce devis"}
          </button>

          {/* iter132 — Bouton "J'ai une question" → WhatsApp avec message pré-rempli */}
          <a
            href={whatsappQuestionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 w-full py-3 rounded-lg border-2 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 font-semibold flex items-center justify-center gap-2 transition-colors"
            data-testid="devis-whatsapp-question-btn"
          >
            <MessageCircle className="w-5 h-5" />
            J&apos;ai une question (WhatsApp)
          </a>
        </section>

        {/* Footer garage */}
        <section className="text-center text-xs text-gray-500 pt-4 space-y-1">
          <div className="font-semibold text-gray-700">{data.garage.name}</div>
          <div className="flex items-center justify-center gap-1"><MapPin className="w-3 h-3" /> {data.garage.adresse}, {data.garage.npa} {data.garage.localite}</div>
          <div className="flex items-center justify-center gap-3 mt-1">
            <a href={`tel:${data.garage.tel}`} className="inline-flex items-center gap-1 hover:text-[#52a675]"><Phone className="w-3 h-3" /> {data.garage.tel}</a>
            <a href={`mailto:${data.garage.email}`} className="inline-flex items-center gap-1 hover:text-[#52a675]"><Mail className="w-3 h-3" /> {data.garage.email}</a>
          </div>
          <div className="pt-2 text-gray-400">Devis émis le {fmtDate(data.date_emission)} · valable jusqu&apos;au {fmtDate(data.expires_at)}</div>
        </section>
      </main>
    </div>
  );
}
